<template>
    <div class="feedback-notification-root">
        <FeedbackContainer>
            <FeedbackTrigger @click="toggleFeedback">
                <FeedbackCount v-if="unreadCount > 0">{{ unreadCount }}</FeedbackCount>
                <div v-if="latestFeedback" class="feedback-preview">
                    <FeedbackUser>
                        <UserAvatar>
                            <AccountCircleIcon :size="30" />
                        </UserAvatar>
                        <FeedbackPreviewContent>
                            <FeedbackAuthor :style="{ filter: isTestUser ? 'blur(4px)' : 'none' }">{{ latestFeedback.author }}
                            </FeedbackAuthor>
                            <FeedbackPreviewMessage>{{ latestFeedback.title }}</FeedbackPreviewMessage>
                        </FeedbackPreviewContent>
                    </FeedbackUser>
                    <PreviewMoreIcon :class="{ 'is-open': isOpen }">
                        <ChevronDownIcon v-if="!isOpen" :size="20" />
                        <ChevronUpIcon v-else :size="20" />
                    </PreviewMoreIcon>
                </div>
                <FeedbackDefault v-else>
                    <FeedbackIcon>
                        <MessageOutlineIcon :size="24" />
                    </FeedbackIcon>
                    <FeedbackText>피드백</FeedbackText>
                </FeedbackDefault>
            </FeedbackTrigger>

            <FeedbackPopup v-if="isOpen">
                <FeedbackHeader>
                    <HeaderTitle>
                        <MessageOutlineIcon :size="18" />
                        피드백 알림
                    </HeaderTitle>
                    <HeaderControls>
                        <SortButton @click="toggleSort" :class="{ active: sortAscending }">
                            <ArrowUpIcon :size="18" v-if="sortAscending" />
                            <ArrowDownIcon :size="18" v-else />
                            {{ sortAscending ? '과거순' : '최신순' }}
                        </SortButton>
                        <CloseButton @click="toggleFeedback">
                            <CloseIcon :size="24" />
                        </CloseButton>
                    </HeaderControls>
                </FeedbackHeader>

                <FeedbackContent>
                    <FeedbackItem v-for="feedback in displayedFeedbacks" :key="feedback.id"
                        :class="{ unread: !feedback.isRead }">
                        <FeedbackItemHeader>
                            <FeedbackUser>
                                <UserAvatar>
                                    <AccountCircleIcon :size="30" />
                                </UserAvatar>
                                <FeedbackAuthor :style="{ filter: isTestUser ? 'blur(4px)' : 'none' }">{{ feedback.author }}
                                </FeedbackAuthor>
                            </FeedbackUser>
                            <FeedbackDate>{{ feedback.createdAt }}</FeedbackDate>
                        </FeedbackItemHeader>

                        <FeedbackTitle>{{ feedback.title }}</FeedbackTitle>
                        <FeedbackMessageWrapper>
                            <FeedbackMessage>{{ feedback.message }}</FeedbackMessage>
                        </FeedbackMessageWrapper>

                        <FeedbackActions>
                            <ActionButton v-if="!feedback.isRead" @click="markAsRead(feedback.id)" class="read">
                                <CheckIcon :size="16" />
                                완료 표시
                            </ActionButton>
                            <ActionButton v-else @click="markAsUnread(feedback.id)" class="unread">
                                <UndoIcon :size="16" />
                                완료 취소
                            </ActionButton>
                            <ActionButton @click="goToOrder(feedback.orderId)" class="order">
                                <FileDocumentOutlineIcon :size="16" />
                                주문서 이동
                            </ActionButton>
                        </FeedbackActions>
                    </FeedbackItem>

                    <FeedbackFooter v-if="hasMoreFeedback">
                        <LoadMoreButton @click="loadMore">
                            더보기
                            <ChevronDownIcon :size="16" />
                        </LoadMoreButton>
                    </FeedbackFooter>
                </FeedbackContent>
            </FeedbackPopup>
        </FeedbackContainer>
    </div>

    <!-- *! 주문서 수정 모달 -->
    <ModalPage v-if="showModifyModal" v-model:show="showModifyModal" :height="modal_height" :parent="'order'">
        <OrderModifyPage :orderId="selectedOrder" @order-updated="fetchOrders" @close-modal="closeModal"
            @feedback-changed="handleFeedbackChanged" />
    </ModalPage>
</template>

<script>
import * as FeedbackNotificationCss from '@/assets/styles/order/popup/FeedbackNotificationCss';
import ModalPage from '@/components/layout/ModalPage.vue';
import OrderModifyPage from '@/components/order/OrderModifyPage.vue';

import MessageOutlineIcon from 'vue-material-design-icons/MessageOutline.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import CheckIcon from 'vue-material-design-icons/Check.vue';
import UndoIcon from 'vue-material-design-icons/Undo.vue';
import FileDocumentOutlineIcon from 'vue-material-design-icons/FileDocumentOutline.vue';
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue';
import AccountCircleIcon from 'vue-material-design-icons/AccountCircle.vue';
import ArrowUpIcon from 'vue-material-design-icons/ArrowUp.vue';
import ArrowDownIcon from 'vue-material-design-icons/ArrowDown.vue';
import ChevronUpIcon from 'vue-material-design-icons/ChevronUp.vue';

export default {
    name: 'FeedbackNotification',

    components: {
        ...FeedbackNotificationCss,
        ModalPage,
        OrderModifyPage,
        MessageOutlineIcon, CloseIcon, CheckIcon, UndoIcon, FileDocumentOutlineIcon, ChevronDownIcon, AccountCircleIcon, ArrowUpIcon, ArrowDownIcon, ChevronUpIcon
    },

    data() {
        return {
            isOpen: false,
            feedbacks: [],
            displayLimit: 5,
            unreadCount: 0,
            sortAscending: true,
            // 주문서 수정 모달
            showModifyModal: false,
            modal_height: '80%',
            selectedOrder: null,

        }
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        latestFeedback() {
            return this.feedbacks[0] || null;
        },
        displayedFeedbacks() {
            const sortedFeedbacks = [...this.feedbacks];

            // 날짜 정렬 로직 개선
            sortedFeedbacks.sort((a, b) => {
                const dateA = new Date(a.createdAt.replace(' ', 'T'));
                const dateB = new Date(b.createdAt.replace(' ', 'T'));

                return this.sortAscending ? dateA - dateB : dateB - dateA;
            });

            return sortedFeedbacks.slice(0, this.displayLimit);
        },
        hasMoreFeedback() {
            return this.feedbacks.length > this.displayLimit;
        }
    },

    async created() {
        await this.fetchFeedbacks();

        // 피드백 업데이트 이벤트 리스너 추가
        window.addEventListener('feedback-updated', this.handleFeedbackUpdate);
    },

    beforeUnmount() {
        // 컴포넌트 해제 시 이벤트 리스너 제거
        window.removeEventListener('feedback-updated', this.handleFeedbackUpdate);
    },

    methods: {
        // 피드백 변경 핸들러 추가
        async handleFeedbackChanged(changed) {
            if (changed) {
                await this.fetchFeedbacks();
            }
        },

        async handleFeedbackUpdate() {
            await this.fetchFeedbacks();
        },

        async fetchOrders() {
            try {
                if (this.activeStatus === 5) {
                    await this.fetchReturnOrders();
                    return;
                }
                if (this.activeStatus === 6) {
                    await this.fetchAsOrders();
                    return;
                }

                this.orders = [];
                const token = localStorage.getItem('token');

                // 검색 파라미터 구성
                const params = {
                    page: this.currentPage,
                    sdate: this.repace_date_hipen(this.startDate),
                    edate: this.repace_date_hipen(this.endDate),
                    contentsSize: this.contentsSize,
                    search: this.searchQuery,
                    companykey: this.$globalState.company_key,
                };

                // 바코드나 품목명 검색이 있는 경우 해당 조건만 사용
                if (this.searchParams?.bcode || this.searchParams?.name3) {
                    if (this.searchParams.bcode) {
                        params.barcode = this.searchParams.bcode.trim();
                    }
                    if (this.searchParams.name3) {
                        params.name3 = this.searchParams.name3.trim();
                    }
                } else {
                    // 일반 검색 조건 추가
                    if (this.searchQuery) {
                        params.search = this.searchQuery;
                    }

                    // 기타 검색 파라미터 추가
                    if (this.searchParams) {
                        Object.entries(this.searchParams).forEach(([key, value]) => {
                            if (value !== null && value !== undefined && value !== '' &&
                                key !== 'bcode' && key !== 'name3') {
                                params[key] = value;
                            }
                        });
                    }
                }

                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/orders`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    params: params
                });

                if (response.data.orders && response.data.orders.length > 0) {
                    this.orders = response.data.orders.map(order => ({
                        ...order,
                        target: this.formatTarget(order),
                        selected: false
                    }));
                    this.currentPage = response.data.currentPage;
                    this.totalPages = response.data.totalPages;
                    this.totalOrders = response.data.totalOrders;
                    this.status_cnt = response.data.status_cnt;
                    this.selectAll = false;
                } else {
                    let message = '';
                    if (this.searchParams.bcode) {
                        message = `바코드 '${this.searchParams.bcode}'에 대한 검색 결과가 없습니다.`;
                    } else if (this.searchParams.name3) {
                        message = `품목명 '${this.searchParams.name3}'에 대한 검색 결과가 없습니다.`;
                    } else if (this.searchQuery) {
                        message = `'${this.searchQuery}'에 대한 검색 결과가 없습니다.`;
                    } else {
                        message = '검색 결과가 없습니다.';
                    }

                    this.$swal.fire({
                        title: '검색 결과 없음',
                        text: message,
                        icon: 'info',
                        confirmButtonText: '확인'
                    });

                    // 상태 초기화
                    this.orders = [];
                    this.currentPage = 1;
                    this.totalPages = 0;
                    this.totalOrders = 0;
                    this.selectAll = false;
                }

                this.saveState();
            } catch (error) {
                console.error('주문 데이터를 가져오는 중 오류가 발생했습니다:', error);
                this.$swal.fire({
                    title: '오류 발생',
                    text: '주문 데이터를 불러오는 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },

        toggleSort() {
            this.sortAscending = !this.sortAscending;
            console.log('정렬 방식 변경:', this.sortAscending ? '오름차순' : '내림차순');
        },

        async fetchFeedbacks() {
            try {
                const token = localStorage.getItem('token');

                if (!this.$globalState.company_key) {
                    console.warn('회사 키가 설정되지 않았습니다.');
                    return;
                }

                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getAllFeedbacks`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.status === 'success' && response.data.data.success) {
                    this.feedbacks = response.data.data.data.map(feedback => ({
                        id: feedback.id,
                        author: feedback.manager,
                        title: feedback.title,
                        message: feedback.content,
                        createdAt: this.formatDate(feedback.createdAt),
                        isRead: feedback.readStatus === 1,
                        orderId: feedback.orderId,
                        commentId: feedback.commentId
                    }));

                    this.unreadCount = this.feedbacks.filter(f => !f.isRead).length;
                }
            } catch (error) {
                console.error('피드백을 불러오는 중 오류 발생:', error);

                // 에러 상세 정보 로깅
                if (error.response) {
                    console.error('에러 응답:', {
                        status: error.response.status,
                        data: error.response.data,
                        companykey: this.$globalState.company_key
                    });
                }
            }
        },

        toggleFeedback() {
            this.isOpen = !this.isOpen;
        },

        loadMore() {
            this.displayLimit += 5;
        },

        async markAsRead(feedbackId) {
            try {
                const token = localStorage.getItem('token');
                const response = await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/updateFeedback/${feedbackId}`,
                    { readStatus: 1 },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.data.status === 'success') {
                    const feedback = this.feedbacks.find(f => f.id === feedbackId);
                    if (feedback) {
                        feedback.isRead = true;
                        this.unreadCount = this.feedbacks.filter(f => !f.isRead).length;
                    }
                } else {
                    throw new Error(response.data.message);
                }
            } catch (error) {
                console.error('피드백 상태 업데이트 중 오류 발생:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류가 발생했습니다.',
                    text: '피드백 상태를 업데이트하는 중 문제가 발생했습니다.'
                });
            }
        },

        async markAsUnread(feedbackId) {
            try {
                const token = localStorage.getItem('token');
                await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/updateFeedback/${feedbackId}`,
                    { readStatus: 0 },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const feedback = this.feedbacks.find(f => f.id === feedbackId);
                if (feedback) {
                    feedback.isRead = false;
                    this.unreadCount = this.feedbacks.filter(f => !f.isRead).length;
                }
            } catch (error) {
                console.error('피드백 상태 업데이트 중 오류 발생:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류가 발생했습니다.',
                    text: '피드백 상태를 업데이트하는 중 문제가 발생했습니다.'
                });
            }
        },

        async goToOrder(orderId) {
            try {
                this.selectedOrder = orderId;
                this.showModifyModal = true;
                this.isOpen = false;
            } catch (error) {
                console.error('주문서 모달 열기 중 오류 발생:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '오류가 발생했습니다.',
                    text: '주문서를 불러오는 중 문제가 발생했습니다.'
                });
            }
        },

        async closeModal() {
            this.showModifyModal = false;
            this.selectedOrder = null;
        },
    },
}
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>